import { useMemo } from 'react'
import { MdTrendingUp, MdTrendingDown, MdWarning, MdCheckCircle } from 'react-icons/md'

function PilotCharts({ pilot }) {
  const chartData = useMemo(() => {
    if (!pilot) return null

    const getDaysUntilExpiry = (dateString) => {
      if (!dateString) return null
      const expiryDate = new Date(dateString)
      const today = new Date()
      const diffTime = expiryDate - today
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      return diffDays
    }

    const getExpiryStatus = (dateString) => {
      const daysUntil = getDaysUntilExpiry(dateString)
      if (daysUntil === null) return 'unknown'
      if (daysUntil < 0) return 'expired'
      if (daysUntil <= 30) return 'warning'
      return 'good'
    }

    const licenseExpiryStatus = getExpiryStatus(pilot.license?.expiry)
    const medicalExpiryStatus = getExpiryStatus(pilot.medical?.nextMedical)
    const simulatorExpiryStatus = getExpiryStatus(pilot.training?.nextSimulatorCheck)
    const trainingExpiryStatus = getExpiryStatus(pilot.training?.nextTraining)

    // Calculate training progress
    const lastTrainingDate = pilot.training?.lastTraining
      ? new Date(pilot.training.lastTraining)
      : null
    const nextTrainingDate = pilot.training?.nextTraining
      ? new Date(pilot.training.nextTraining)
      : null
    let trainingProgress = 0

    if (lastTrainingDate && nextTrainingDate) {
      const totalDays = 180 // Assuming 6 months between trainings
      const daysSinceLastTraining = Math.floor(
        (new Date() - lastTrainingDate) / (1000 * 60 * 60 * 24),
      )
      trainingProgress = Math.min((daysSinceLastTraining / totalDays) * 100, 100)
    }

    // Calculate simulator progress
    const lastSimulatorDate = pilot.training?.lastSimulatorCheck
      ? new Date(pilot.training.lastSimulatorCheck)
      : null
    const nextSimulatorDate = pilot.training?.nextSimulatorCheck
      ? new Date(pilot.training.nextSimulatorCheck)
      : null
    let simulatorProgress = 0

    if (lastSimulatorDate && nextSimulatorDate) {
      const totalDays = 180 // Assuming 6 months between simulator checks
      const daysSinceLastSimulator = Math.floor(
        (new Date() - lastSimulatorDate) / (1000 * 60 * 60 * 24),
      )
      simulatorProgress = Math.min((daysSinceLastSimulator / totalDays) * 100, 100)
    }

    return {
      licenseExpiryStatus,
      medicalExpiryStatus,
      simulatorExpiryStatus,
      trainingExpiryStatus,
      trainingProgress,
      simulatorProgress,
      daysUntilLicense: getDaysUntilExpiry(pilot.license?.expiry),
      daysUntilMedical: getDaysUntilExpiry(pilot.medical?.nextMedical),
      daysUntilSimulator: getDaysUntilExpiry(pilot.training?.nextSimulatorCheck),
      daysUntilTraining: getDaysUntilExpiry(pilot.training?.nextTraining),
    }
  }, [pilot])

  if (!chartData) {
    return (
      <div className="text-center py-8">
        <p className="text-neutral-600 dark:text-neutral-400">No data available for charts</p>
      </div>
    )
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'good':
        return <MdCheckCircle className="text-success-600 dark:text-success-400" />
      case 'warning':
        return <MdWarning className="text-warning-600 dark:text-warning-400" />
      case 'expired':
        return <MdTrendingDown className="text-red-600 dark:text-red-400" />
      default:
        return <MdTrendingUp className="text-neutral-500 dark:text-neutral-400" />
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'good':
        return 'text-success-600 dark:text-success-400'
      case 'warning':
        return 'text-warning-600 dark:text-warning-400'
      case 'expired':
        return 'text-red-600 dark:text-red-400'
      default:
        return 'text-neutral-500 dark:text-neutral-400'
    }
  }

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'bg-red-500'
    if (progress >= 60) return 'bg-warning-500'
    return 'bg-success-500'
  }

  return (
    <div className="space-y-6">
      {/* Certification Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-neutral-600 dark:text-neutral-400">License</h4>
            {getStatusIcon(chartData.licenseExpiryStatus)}
          </div>
          <p className={`text-lg font-semibold ${getStatusColor(chartData.licenseExpiryStatus)}`}>
            {chartData.daysUntilLicense === null
              ? 'N/A'
              : chartData.daysUntilLicense < 0
                ? 'Expired'
                : `${chartData.daysUntilLicense} days`}
          </p>
        </div>

        <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-neutral-600 dark:text-neutral-400">Medical</h4>
            {getStatusIcon(chartData.medicalExpiryStatus)}
          </div>
          <p className={`text-lg font-semibold ${getStatusColor(chartData.medicalExpiryStatus)}`}>
            {chartData.daysUntilMedical === null
              ? 'N/A'
              : chartData.daysUntilMedical < 0
                ? 'Expired'
                : `${chartData.daysUntilMedical} days`}
          </p>
        </div>

        <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
              Simulator
            </h4>
            {getStatusIcon(chartData.simulatorExpiryStatus)}
          </div>
          <p className={`text-lg font-semibold ${getStatusColor(chartData.simulatorExpiryStatus)}`}>
            {chartData.daysUntilSimulator === null
              ? 'N/A'
              : chartData.daysUntilSimulator < 0
                ? 'Expired'
                : `${chartData.daysUntilSimulator} days`}
          </p>
        </div>

        <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-neutral-600 dark:text-neutral-400">Training</h4>
            {getStatusIcon(chartData.trainingExpiryStatus)}
          </div>
          <p className={`text-lg font-semibold ${getStatusColor(chartData.trainingExpiryStatus)}`}>
            {chartData.daysUntilTraining === null
              ? 'N/A'
              : chartData.daysUntilTraining < 0
                ? 'Expired'
                : `${chartData.daysUntilTraining} days`}
          </p>
        </div>
      </div>

      {/* Progress Bars */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Training Progress */}
        <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4">
          <h4 className="text-sm font-medium text-neutral-600 dark:text-neutral-400 mb-3">
            Training Progress
          </h4>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-neutral-600 dark:text-neutral-400">Progress</span>
              <span className="text-neutral-900 dark:text-white font-medium">
                {Math.round(chartData.trainingProgress)}%
              </span>
            </div>
            <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(chartData.trainingProgress)}`}
                style={{ width: `${chartData.trainingProgress}%` }}
              />
            </div>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              {chartData.daysUntilTraining === null
                ? 'No training schedule'
                : chartData.daysUntilTraining < 0
                  ? 'Training overdue'
                  : `${chartData.daysUntilTraining} days until next training`}
            </p>
          </div>
        </div>

        {/* Simulator Progress */}
        <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4">
          <h4 className="text-sm font-medium text-neutral-600 dark:text-neutral-400 mb-3">
            Simulator Check Progress
          </h4>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-neutral-600 dark:text-neutral-400">Progress</span>
              <span className="text-neutral-900 dark:text-white font-medium">
                {Math.round(chartData.simulatorProgress)}%
              </span>
            </div>
            <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(chartData.simulatorProgress)}`}
                style={{ width: `${chartData.simulatorProgress}%` }}
              />
            </div>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              {chartData.daysUntilSimulator === null
                ? 'No simulator schedule'
                : chartData.daysUntilSimulator < 0
                  ? 'Simulator check overdue'
                  : `${chartData.daysUntilSimulator} days until next simulator check`}
            </p>
          </div>
        </div>
      </div>

      {/* Flight Hours Distribution */}
      {pilot.flightExperience && (
        <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4">
          <h4 className="text-sm font-medium text-neutral-600 dark:text-neutral-400 mb-3">
            Flight Experience Summary
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                {pilot.flightExperience.totalFlightHours?.toLocaleString() || '0'}
              </p>
              <p className="text-xs text-neutral-600 dark:text-neutral-400">Total Hours</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                {pilot.training?.simulatorHours?.toLocaleString() || '0'}
              </p>
              <p className="text-xs text-neutral-600 dark:text-neutral-400">Simulator Hours</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                {pilot.flightExperience.typeRatings?.length || '0'}
              </p>
              <p className="text-xs text-neutral-600 dark:text-neutral-400">Type Ratings</p>
            </div>
          </div>
        </div>
      )}

      {/* Alerts */}
      <div className="space-y-2">
        {chartData.licenseExpiryStatus === 'expired' && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <MdWarning className="text-red-600 dark:text-red-400" />
              <span className="text-red-800 dark:text-red-200 font-medium">
                License has expired
              </span>
            </div>
          </div>
        )}

        {chartData.medicalExpiryStatus === 'expired' && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <MdWarning className="text-red-600 dark:text-red-400" />
              <span className="text-red-800 dark:text-red-200 font-medium">
                Medical certificate has expired
              </span>
            </div>
          </div>
        )}

        {chartData.licenseExpiryStatus === 'warning' && (
          <div className="bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <MdWarning className="text-warning-600 dark:text-warning-400" />
              <span className="text-warning-800 dark:text-warning-200 font-medium">
                License expires in {chartData.daysUntilLicense} days
              </span>
            </div>
          </div>
        )}

        {chartData.medicalExpiryStatus === 'warning' && (
          <div className="bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <MdWarning className="text-warning-600 dark:text-warning-400" />
              <span className="text-warning-800 dark:text-warning-200 font-medium">
                Medical certificate expires in {chartData.daysUntilMedical} days
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default PilotCharts
